// Thin fetch wrappers for the public booking endpoints exposed by the admin
// backend (admin.seadent.es). Cross-origin from seadent.es; CORS is configured
// server-side. No auth — these endpoints are intentionally unauthenticated.

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://admin.seadent.es/api'
  : 'http://localhost:5000/api';

export interface AvailabilityDay {
  date: string;            // 'YYYY-MM-DD'
  hasSlots: boolean;
  count: number;
}

export interface AvailabilitySlot {
  start_time: string;      // 'HH:mm'
  end_time: string;
}

export interface HoldResponse {
  holdToken: string;
  expiresAt: string;       // ISO
  end_time: string;
}

const buildQuery = (params: Record<string, string | number | undefined>): string => {
  const u = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') u.set(k, String(v));
  });
  return u.toString();
};

const ProcedureRefSchema = (procedureId?: number, procedureKey?: string) => {
  if (!procedureId && !procedureKey) {
    throw new Error('Either procedureId or procedureKey is required');
  }
  return { procedureId, procedureKey };
};

export async function getAvailabilityRange(opts: {
  procedureId?: number;
  procedureKey?: string;
  from: string;
  to: string;
}): Promise<AvailabilityDay[]> {
  const ref = ProcedureRefSchema(opts.procedureId, opts.procedureKey);
  const qs = buildQuery({ ...ref, from: opts.from, to: opts.to });
  const r = await fetch(`${API_BASE_URL}/public/availability?${qs}`);
  if (!r.ok) throw new Error(`availability range failed: ${r.status}`);
  const j = await r.json();
  return Array.isArray(j.days) ? j.days : [];
}

export async function getAvailabilityDay(opts: {
  procedureId?: number;
  procedureKey?: string;
  date: string;
}): Promise<AvailabilitySlot[]> {
  const ref = ProcedureRefSchema(opts.procedureId, opts.procedureKey);
  const qs = buildQuery({ ...ref, date: opts.date });
  const r = await fetch(`${API_BASE_URL}/public/availability/day?${qs}`);
  if (!r.ok) throw new Error(`availability day failed: ${r.status}`);
  const j = await r.json();
  return Array.isArray(j.slots) ? j.slots : [];
}

export async function createHold(opts: {
  procedureId?: number;
  procedureKey?: string;
  date: string;
  time: string;
}): Promise<HoldResponse> {
  const ref = ProcedureRefSchema(opts.procedureId, opts.procedureKey);
  const r = await fetch(`${API_BASE_URL}/public/holds`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: JSON.stringify({ ...ref, date: opts.date, time: opts.time }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err: any = new Error(j?.message || `hold failed: ${r.status}`);
    err.status = r.status;
    err.code = j?.reason || j?.code;
    throw err;
  }
  return { holdToken: j.holdToken, expiresAt: j.expiresAt, end_time: j.end_time };
}

export async function releaseHold(token: string): Promise<void> {
  // Best-effort: ignore failures, the hold expires on its own.
  try {
    await fetch(`${API_BASE_URL}/public/holds/${encodeURIComponent(token)}`, { method: 'DELETE' });
  } catch { /* ignore */ }
}
